import { projectContactOntoRow } from "components/_dashboard/crm/panel/contactRowProjection";
import { DOCUMENT_STALE_AFTER_MS, normalizeBookKey, ORG_BOOK_KEY_PREFIX } from "./contactDocumentStore";
import type { ContactDocumentEntry, ContactFetchTrigger } from "./contactDocumentStore";
import type { ContactDocumentSource } from "./contactDocumentSource";
import type { RealEstateContact, REST_schema } from "../../@types/sharedSchema";
import type { CrmContactsBulkCreateResponse } from "../../@types/webotServer";

/**
 * THE DECISIONS BEHIND `useContactDocument` AND `useContactByPhone` — and, at the end, what an
 * import means to the document store, for `useContactWrites` — kept pure so they can be
 * tested without React or Firebase (the repo's jest cannot import a context). The hooks
 * are the wiring; everything that decides lives here.
 */

/** While the signal listener is in error, a mounted document is polled this often. */
export const SIGNAL_ERROR_POLL_MS = 30 * 1000;

/** The shadow comparison waits this long after either side moved — the mirror's lag window. */
export const SHADOW_SETTLE_MS = 2 * 1000;

// ---------------------------------------------------------------------------
// Signal documents
// ---------------------------------------------------------------------------

/** The contact's signal, with `at` as a Date (the web SDK hands it over as a Timestamp). */
export type ContactSignal = { v: number; at: Date; deleted: boolean };

/**
 * The Firestore document a book's satellites hang off: the owner's user document for a
 * personal book, the agency's document for a pooled one (plan D4). The signals below and
 * the per-contact `documents` subcollection (`documentsData.ts`) both build on it, so an
 * `org:` key resolves to the same root everywhere. The key is interpolated as given: this
 * is a Firestore path, addressed like every other `dashboard-users-v2/{user.email}` path in
 * the dashboard; the store's folding (`normalizeBookKey`) is for its own map.
 */
export const contactBookRootPath = (bookKey: string): string =>
  bookKey.startsWith(ORG_BOOK_KEY_PREFIX)
    ? `dashboard-orgs/${bookKey.slice(ORG_BOOK_KEY_PREFIX.length)}`
    : `dashboard-users-v2/${bookKey}`;

/**
 * Where a book's signal documents live. Both paths are read-only for clients
 * (`firestore.rules`) — the server's outbox drain is the only writer.
 */
export const contactSignalsCollectionPath = (bookKey: string): string => `${contactBookRootPath(bookKey)}/contact-signals`;

export const contactSignalDocPath = (bookKey: string, contactId: string): string =>
  `${contactSignalsCollectionPath(bookKey)}/${contactId}`;

/**
 * A signal document's data (dates already converted to `Date`) as a signal, or null when it
 * is not one: the hook must never compare against a malformed `v`, and a document with no
 * usable version is the same as no document.
 */
export const toContactSignal = (raw: unknown): ContactSignal | null => {
  const data = raw as { v?: unknown; at?: unknown; deleted?: unknown } | null | undefined;
  if (!data || typeof data.v !== "number" || !Number.isFinite(data.v)) return null;
  const at =
    data.at instanceof Date
      ? data.at
      : new Date(typeof data.at === "string" || typeof data.at === "number" ? data.at : 0);
  return { v: data.v, at: Number.isNaN(at.getTime()) ? new Date(0) : at, deleted: data.deleted === true };
};

/** One spelling of a phone and the signal document found under it. */
export type CandidateSignal = { contactId: string; signal: ContactSignal };

/**
 * A phone that is NOT a contact is watched through the signals of its spellings (one
 * listener, `subscribeCandidateSignals`). Is there NEWS in what that listener just
 * delivered: a signal saying one of them EXISTS, in a state this reader has not acted on?
 * `seen` is the reader's memory (contact id → the last signal it saw) and is updated here.
 *
 * Compared by the signal's whole state, never by `v` alone: a contact that is deleted and
 * created again restarts at 1. A signal that says "deleted" is remembered and is not news,
 * and the same signal delivered twice (the cache, then the server) is news once.
 */
export const hasLiveCandidateNews = (seen: Map<string, string>, signals: readonly CandidateSignal[]): boolean => {
  let news = false;
  for (const { contactId, signal } of signals) {
    const state = `${signal.v}|${signal.at.getTime()}|${signal.deleted ? "deleted" : "live"}`;
    if (seen.get(contactId) === state) continue;
    seen.set(contactId, state);
    if (!signal.deleted) news = true;
  }
  return news;
};

// ---------------------------------------------------------------------------
// When to ask
// ---------------------------------------------------------------------------

/** An answer older than the clock allows, or none at all. */
export const isStale = (entry: ContactDocumentEntry, now: number): boolean =>
  entry.fetchedAt === 0 || now - entry.fetchedAt >= DOCUMENT_STALE_AFTER_MS;

/**
 * Should a reader that is mounting (or waking: a tab becoming visible, an invalidation)
 * send a request? Yes when nobody asked, when the last ask failed and nothing is held, or
 * when what is held is stale — and never while a request is already out.
 */
export const shouldFetch = (entry: ContactDocumentEntry | undefined, now: number): boolean => {
  if (!entry) return true;
  if (entry.inFlight) return false;
  return entry.state === "resolving" || isStale(entry, now);
};

/**
 * Does what is held answer a reader asking under `scope`? Everything does but one thing: an
 * absence READ UNDER `mine` (`absentUnderMine`), for a reader asking under `org`.
 *
 * The store files a pooled book's documents under the office's key for both scopes, and
 * `mine` is narrowed there to the viewer's own list. So a phone check on a colleague's
 * customer (a seller badge, a "this number exists" check) settles the entry `absent`, and a
 * panel that then opens the office's record under `org` was handed that answer for a minute
 * (`DOCUMENT_STALE_AFTER_MS`) — "הלקוח לא נמצא" for a customer the office has. Such a reader
 * asks again, and reads the entry as resolving until its own answer lands.
 */
export const entryAnswersScope = (entry: ContactDocumentEntry | undefined, scope: ContactReadScope): boolean =>
  !(entry?.state === "absent" && entry.absentUnderMine && scope === "org");

export type SignalVerdict = "none" | "fetch" | "absent";

/**
 * What a signal means for what is held. The server bumps `crm_contact.version` on every
 * write and the signal carries that number, so a signal AT the version held (or at the one
 * already acted on, `signalV`) says nothing new. A newer one with `deleted` is the contact
 * going away — no fetch needed; any other newer one is a fetch. While a request is out the
 * verdict waits: the hook re-asks when it lands, because that request may have read the
 * document before the write the signal is about — and a write from this browser claims the
 * entry before its request leaves, so its own signal, which the server writes inside the
 * request and which usually arrives BEFORE the response, waits for the response instead of
 * costing a fetch that the response then overrides.
 *
 * A version that went BACKWARDS is a RESET, and is acted on. The server starts a re-created
 * contact again at version 1 (the delete carried the old version + 1, and the planner keeps
 * the latest outbox row for exactly this reason), and Firestore may coalesce the delete and
 * the re-create into one snapshot. Read as "nothing new", a lower `v` left a card on "not a
 * contact" (or on the deleted contact's old document) with every later edit — v=2, 3, … —
 * swallowed until the count passed the old ceiling. The one other way `v` goes backwards is
 * two outbox passes crossing on one contact, and refetching there costs one request.
 */
export const signalVerdict = (signal: ContactSignal | null, entry: ContactDocumentEntry | undefined): SignalVerdict => {
  if (!signal || !entry || entry.inFlight) return "none";
  if (signal.v === Math.max(entry.version, entry.signalV)) return "none";
  return signal.deleted ? "absent" : "fetch";
};

/** How often to poll while the signal listener is broken; null when it is not. */
export const pollIntervalMs = (signalError: Error | null): number | null => (signalError ? SIGNAL_ERROR_POLL_MS : null);

// ---------------------------------------------------------------------------
// The request
// ---------------------------------------------------------------------------

/**
 * Which scope a document reader asks under. `mine` is the caller's own book; `org` is the
 * agency view, which a pooled member's office-tab panel needs for a record they do NOT hold
 * — under `mine` the server narrows the office book to the caller's own list, so an unheld
 * record is "not found" there and found (at the agency's pool level) under `org`.
 */
export type ContactReadScope = "mine" | "org";

export type ContactReadOptions = { scope?: ContactReadScope; orgId?: string | null };

/**
 * The scope half of a `POST /contacts/get` request for a book. The caller's own book needs
 * nothing (the endpoint defaults to it); any other book is named as its owner under the
 * agency view, and the server intersects it with the caller's resolved scope — naming a
 * book can narrow what the caller may see, never widen it.
 *
 * `ownBook` is the caller's OWN book key — the email for a personal book, the office's
 * `org:` key for a pooled member (`useContactBook().ownBookKey`). Comparing against the
 * email alone sent a pooled member's own office record as `view: "org"`, which the server
 * refuses to a member without `browse`. An explicit `scope: "org"` wins over the comparison
 * (the office tab's unheld record — see `ContactReadScope`).
 */
export const contactGetScope = (
  bookKey: string,
  ownBook: string | null,
  read?: ContactReadOptions
): Pick<REST_schema.CrmContactGetRequest, "ownerUserId" | "view" | "orgId"> => {
  if (read?.scope === "org") {
    return { ownerUserId: bookKey, view: "org", ...(read.orgId ? { orgId: read.orgId } : {}) };
  }
  return ownBook && normalizeBookKey(bookKey) === normalizeBookKey(ownBook) ? {} : { ownerUserId: bookKey, view: "org" };
};

/** From the prompt to the answer: a signal's `at` when the fetch was made for one, else the request leaving. */
export const fetchLatencyMs = (answeredAt: number, startedAt: number, signalAt: Date | undefined): number =>
  Math.max(0, answeredAt - (signalAt ? signalAt.getTime() : startedAt));

// ---------------------------------------------------------------------------
// Reviving a document that crossed the wire
// ---------------------------------------------------------------------------

/**
 * The contact's `Date`-typed fields. The server reads the document through the admin
 * converter (Timestamps → Dates) and `res.json` turns those into ISO strings, so a document
 * from `/contacts/get`, `/get-many`, or the `doc` of a write response arrives with strings
 * where the listener's converter hands back Dates. These four are the ones the type declares
 * as `Date` on the document itself; `lastBulkMessageReceived.sentTime` is typed — and
 * written — as a string and is left alone.
 */
export const CONTACT_DOCUMENT_DATE_FIELDS = [
  "createdTime",
  "updatedTime",
  "deletedAt",
  "skipUpdatedTimeAroundDate"
] as const;

/**
 * A copy of the document with its date fields as Dates, so it reads exactly like one the
 * Firestore converter produced. A value that is not an ISO date string is kept as it is.
 */
export const reviveContactDocument = <T extends Partial<RealEstateContact>>(doc: T): T => {
  const revived: Record<string, unknown> = { ...doc };
  for (const field of CONTACT_DOCUMENT_DATE_FIELDS) {
    const value = revived[field];
    if (typeof value !== "string") continue;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) revived[field] = parsed;
  }
  return revived as T;
};

// ---------------------------------------------------------------------------
// A phone's candidates
// ---------------------------------------------------------------------------

export type CandidatesResolution =
  | { kind: "resolving" }
  | { kind: "absent"; stale: boolean }
  | { kind: "present"; contactId: string };

/**
 * What the store says about a phone asked by every spelling it could be filed under, in
 * order of preference: the first spelling that is `present` is the contact (even stale —
 * the by-id reader refreshes it); every spelling known `absent` is "not a contact" (stale
 * when any of those answers is, so the phone is asked again while that still renders);
 * anything else is still resolving.
 */
export const resolveCandidates = (
  candidates: readonly string[],
  entryOf: (contactId: string) => ContactDocumentEntry | undefined,
  now: number
): CandidatesResolution => {
  let allAbsent = candidates.length > 0;
  let stale = false;
  for (const contactId of candidates) {
    const entry = entryOf(contactId);
    if (entry?.state === "present") return { kind: "present", contactId };
    if (entry?.state === "absent") {
      stale = stale || isStale(entry, now);
    } else {
      allAbsent = false;
    }
  }
  return allAbsent ? { kind: "absent", stale } : { kind: "resolving" };
};

/** What a prompt does about a phone: send the request, wait for one that is out, or nothing. */
export type CandidatesAskDecision = "ask" | "stand_back" | "rest";

/**
 * A PROMPT has come for a phone (the mount, an invalidation, the tab coming back, the found
 * contact going away, news from the watch on its spellings). What now?
 *
 *  - `rest`        a contact is held, or "not a contact" is fresh and nothing says otherwise;
 *  - `stand_back`  the phone should be asked about, but a request is already out for one of
 *                  its spellings — a panel reading the same contact by id, another card on
 *                  the same phone, or this reader's own earlier request. No second request
 *                  over it; the reader looks again when nothing is out;
 *  - `ask`         send it.
 *
 * `newsPending`: the watch saw a live contact under one of the spellings and no request that
 * LEFT after it has answered. It overrides a fresh "not a contact" — which is exactly what
 * the news contradicts — and it is why standing back is right even for a request of our own:
 * that request read the phone before the news.
 */
export const decideCandidatesAsk = (
  held: CandidatesResolution,
  anyInFlight: boolean,
  newsPending: boolean
): CandidatesAskDecision => {
  if (held.kind === "present") return "rest";
  const wanted = newsPending || held.kind === "resolving" || held.stale;
  if (!wanted) return "rest";
  return anyInFlight ? "stand_back" : "ask";
};

/**
 * ONE phone's answer out of a `get-many` that asked about several phones at once: the first
 * of ITS spellings, in its own order of preference, that came back — the rule the server
 * applies to one phone's `candidates` (`readFirstExisting` in the server's
 * `contactsRead.service.ts`), so a phone asked together with others is answered exactly as if
 * it had been asked alone. Null when none of its spellings came back.
 */
export const firstReturnedCandidate = <T>(
  candidates: readonly string[],
  returned: ReadonlyMap<string, T>
): { contactId: string; entry: T } | null => {
  for (const contactId of candidates) {
    const entry = returned.get(contactId);
    if (entry !== undefined) return { contactId, entry };
  }
  return null;
};

// ---------------------------------------------------------------------------
// Shadow mode: comparing the two mechanisms
// ---------------------------------------------------------------------------

/** A definite answer from either mechanism: the contact exists (with its document) or it does not. */
export type ShadowAnswer = { exists: boolean; doc: RealEstateContact | null };

export type ShadowComparison = { matched: boolean; differences: string[] };

/**
 * The listener's answer against the server's. "Exists" must agree; when both have a
 * document, the fields the CRM row projection reads must agree (`projectContactOntoRow` —
 * the same projection the panel pushes onto its row, so a difference here is one a realtor
 * would see). Nothing else is compared: the server redacts per scope and the mirror's
 * version column is not on the document, so a whole-document equality would report noise.
 */
export const compareShadowAnswers = (listener: ShadowAnswer, server: ShadowAnswer): ShadowComparison => {
  if (listener.exists !== server.exists) return { matched: false, differences: ["exists"] };
  if (!listener.exists || !listener.doc || !server.doc) return { matched: true, differences: [] };
  const expected = projectContactOntoRow(listener.doc) as Record<string, unknown>;
  const actual = projectContactOntoRow(server.doc) as Record<string, unknown>;
  const differences = Object.keys(expected).filter(
    (key) => JSON.stringify(expected[key]) !== JSON.stringify(actual[key])
  );
  return { matched: differences.length === 0, differences };
};

export type ContactDocumentShadowEvent = {
  source: "useContactByPhone" | "useLiveContact";
  mode: ContactDocumentSource;
  matched: boolean;
  differences: string[];
  /** Signal `at` (or the request leaving) → the server's answer, for the answer compared. Null when the server side has not fetched. */
  latencyMs: number | null;
  trigger: ContactFetchTrigger | null;
  settleMs: number;
};

// ---------------------------------------------------------------------------
// What an import means to the store
// ---------------------------------------------------------------------------

/**
 * The ids an import WROTE, as the server filed them — what `useContactWrites` invalidates in
 * the document store once `/bulk-create` is done. Created and updated rows alike, from every
 * chunk that landed; a row the server refused wrote nothing; and a written row is named by
 * its normalized `contactId`, never by the spelling the sheet sent (`requestedContactId`),
 * because the store is keyed by the id the document is filed under.
 *
 * Named, not everything: an import used to invalidate every held document, so every mounted
 * card refetched — the ones the import never touched for nothing, and the imported ones a
 * second time when their own signals landed. A reader of an imported contact may still pay
 * twice (its signal and the invalidation race for the first fetch); nobody else pays at all.
 */
export const contactIdsWrittenByImport = (
  results: ReadonlyArray<Pick<CrmContactsBulkCreateResponse["results"][number], "contactId" | "status">>
): string[] => {
  const written = new Set<string>();
  for (const result of results) {
    if (result.status !== "failed" && result.contactId) written.add(result.contactId);
  }
  return Array.from(written);
};
